import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Thème clair/sombre et position du logo (design-kit : [data-theme] et
// [data-logo] sur <html>). Préférences persistées dans localStorage.
type Theme = 'light' | 'dark';
type LogoPosition = 'top' | 'bottom';
interface ThemeContextValue {
  theme: Theme; toggleTheme: () => void;
  logoPosition: LogoPosition; toggleLogoPosition: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>(() => (localStorage.getItem('logo-position') === 'top' ? 'top' : 'bottom'));

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.setAttribute('data-logo', logoPosition);
    localStorage.setItem('logo-position', logoPosition);
  }, [logoPosition]);

  return (
    <ThemeContext.Provider value={{
      theme, toggleTheme: () => setTheme((t) => (t === 'light' ? 'dark' : 'light')),
      logoPosition, toggleLogoPosition: () => setLogoPosition((p) => (p === 'top' ? 'bottom' : 'top')),
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans ThemeProvider');
  return ctx;
}
